import type {
  ColDef,
  ColGroupDef,
  ColumnEventType,
  GridApi
} from 'ag-grid-community'
import type { ViewHook } from 'phoenix_live_view'
import { filter, flatMap, map, pipe, reduce } from 'remeda'
import type {
  GridColumn,
  GridLeafColumn,
  ProcessColumnsFunction
} from '../utils/column-processors'
import {
  flattenGridColumns,
  pinnedBottomData
} from '../utils/column-processors'
import { gridSortField } from '../utils/sorting-state'

/**
 * Column Update Handlers for AG Grid
 * Handles column visibility, width changes, reordering, and pinned data
 */

// Column update payload structure
type ColumnUpdatePayload = {
  readonly id: string
  readonly data: readonly {
    readonly field: string
    readonly width: number
    readonly visible: boolean
  }[]
}

// Column extraction result
type ColumnData = {
  readonly field: string
  readonly width: number
  readonly visible: boolean
}

type GridColumnDefinition = ColDef | ColGroupDef

/**
 * Check if a field name matches special AG Grid internal column patterns
 *
 * Special columns include:
 * - AG Grid managed columns (checkbox selection, row grouping, pivot)
 * - Custom system columns (drag handle)
 *
 * Pattern Sources:
 * - Observed from AG Grid v34 runtime behavior via browser DevTools
 * - Confirmed by analyzing AG Grid's column definition lifecycle
 */
const matchesSpecialColumnPattern = (field: string): boolean => {
  if (!field || typeof field !== 'string') {
    return false
  }

  // AG Grid internal column patterns (observed from v34 behavior)
  const agGridInternalPatterns = [
    /^ag-Grid-AutoColumn/, // Auto group columns for row grouping
    /^ag-Grid-SelectionColumn/, // Checkbox selection columns (CRITICAL for preventing duplication)
    /^ag-/, // Legacy catch-all for other internal columns
    /ag-checkbox-column/, // Alternative checkbox patterns from older versions
    /ag-selection-column/, // Alternative selection patterns
    /ag-row-group/, // Row grouping columns
    /ag-pivot/ // Pivot mode columns
  ]

  // Check against AG Grid internal patterns
  if (agGridInternalPatterns.some((pattern) => pattern.test(field))) {
    return true
  }

  // Our custom drag handle column for row reordering
  // This is injected by processColumns() when movableRows is enabled
  if (field === '__dragHandle') {
    return true
  }

  return false
}

/** Extract column data for updates using functional composition, filtering out special columns */
export const extractColumnData = (params: {
  api: GridApi
}): readonly ColumnData[] =>
  pipe(
    params.api.getAllGridColumns(),
    // Filter out AG Grid internal columns and drag handle BEFORE sending to backend
    // This prevents duplication when backend processes and returns these columns
    filter((column) => !matchesSpecialColumnPattern(column.getColId())),
    map((column) => ({
      field: column.getColId(),
      width: Math.round(column.getActualWidth()),
      visible: column.isVisible()
    }))
  )

/** Send completed column changes without echoing server updates. */
export const createColumnUpdateHandler =
  (eventName: string, hook: ViewHook, target?: string | null) =>
  (params: {
    readonly api: GridApi<unknown>
    readonly finished: boolean
    readonly source: ColumnEventType
  }): void => {
    // Applying server column definitions also emits finished resize/move events.
    if (params.finished && eventName && params.source !== 'api') {
      const data = extractColumnData(params)
      const payload: ColumnUpdatePayload = { id: hook.el.id, data }

      if (target) {
        hook.pushEventTo(target, eventName, payload as object)
      } else {
        hook.pushEvent(eventName, payload)
      }
    }
  }

/** Update pinned bottom row data in grid with conditional rendering */
export const updatePinnedBottomRowData = (
  grid: GridApi,
  columns: readonly GridColumn[]
): void => {
  const pinnedBottomRowData = pinnedBottomData(columns)
  grid.setGridOption(
    'pinnedBottomRowData',
    Object.keys(pinnedBottomRowData).length > 0
      ? [pinnedBottomRowData]
      : undefined
  )
}

/**
 * Identifies AG Grid's internal special columns that should be excluded from merge operations
 *
 * AG Grid automatically manages certain columns based on grid configuration:
 * - Checkbox selection columns (when rowSelection.checkboxes = true)
 * - Row group columns (when row grouping is enabled)
 * - Pivot columns (in pivot mode)
 *
 * These columns should NEVER be preserved during column merges because:
 * 1. AG Grid adds them automatically based on current configuration
 * 2. Preserving them causes duplication when config doesn't change
 * 3. They have dynamic field names (e.g., ag-Grid-SelectionColumn_1_1)
 */
const isSpecialColumn = (column: ColDef): boolean => {
  const field = column.field

  if (!field || typeof field !== 'string') {
    // Columns without field names are not special columns we need to track
    return false
  }

  // Delegate to shared pattern matching utility
  // This catches AG Grid selection columns (ag-Grid-SelectionColumn) and other internal columns
  return matchesSpecialColumnPattern(field)
}

const isAgColumnGroup = (column: GridColumnDefinition): column is ColGroupDef =>
  'children' in column

const flattenAgColumns = (
  columns: readonly GridColumnDefinition[]
): readonly ColDef[] =>
  pipe(
    columns,
    flatMap((column) =>
      isAgColumnGroup(column)
        ? flattenAgColumns(column.children as readonly GridColumnDefinition[])
        : [column]
    )
  )

/** Create a map of columns by their field property using functional composition */
const createColumnMap = (
  columns: readonly GridColumnDefinition[]
): Map<string, ColDef> =>
  pipe(
    flattenAgColumns(columns),
    filter((col): col is ColDef & { field: string } => Boolean(col.field)),
    map((col) => [col.field, col] as const),
    (entries) => new Map(entries)
  )

/** Merge existing columns with new processed columns, preserving order from payload */
const mergeColumnArrays = (
  existingColumns: readonly GridColumnDefinition[],
  processedNewColumns: readonly GridColumnDefinition[],
  newColumnsMap: Map<string, ColDef>
): readonly GridColumnDefinition[] => {
  // Get existing columns that weren't included in the new payload, excluding special columns
  const preservedColumns = filter(
    flattenAgColumns(existingColumns),
    (col): col is ColDef =>
      typeof col.field === 'string' &&
      col.field.length > 0 &&
      !newColumnsMap.has(col.field) &&
      !isSpecialColumn(col) // Filter out AG Grid internal and drag handle columns
  )

  // Return new columns first (preserves reordering), then preserved columns
  // Special columns (checkbox, drag handle) are managed by AG Grid and processColumns
  return [...processedNewColumns, ...preservedColumns]
}

/** Get safe grid context with fallback defaults */
const getSafeGridContext = (context?: {
  readonly movableRows: boolean
  readonly readonly: boolean
}) => context || { movableRows: false, readonly: false }

/** Merge new columns with existing columns, preserving columns not in the new payload */
const mergeColumns = (
  existingColumns: readonly GridColumnDefinition[],
  newColumns: readonly GridColumn[],
  processColumns: ProcessColumnsFunction,
  gridContext?: { readonly movableRows: boolean; readonly readonly: boolean }
): readonly GridColumnDefinition[] => {
  // Get safe context with fallback defaults
  const safeContext = getSafeGridContext(gridContext)

  // Process new columns with proper context
  const processedNewColumns = processColumns(
    newColumns,
    safeContext.movableRows,
    safeContext.readonly
  )

  // Create map for new columns lookup
  const newColumnsMap = createColumnMap(processedNewColumns)

  // Merge the column arrays
  return mergeColumnArrays(existingColumns, processedNewColumns, newColumnsMap)
}

/**
 * Detect if column changes are visibility-only (no structural changes)
 *
 * Structural changes include:
 * - Column width modifications
 * - Column reordering
 * - Adding/removing columns
 *
 * If only visibility changed, we can use the faster setColumnsVisible() API
 * instead of recreating the entire column structure with setGridOption('columnDefs')
 */
type ColumnChangeType =
  | {
      readonly type: 'visibility-only'
      readonly changes: ReadonlyMap<string, boolean>
    }
  | { readonly type: 'structural' }
  | { readonly type: 'state-only' }

/**
 * Column Change Detection - Pure helper functions for analyzing column updates
 */

/** Create map of current columns excluding special columns */
const createCurrentColumnsMap = (
  columns: readonly GridColumnDefinition[]
): ReadonlyMap<string, ColDef> =>
  new Map(
    filter(
      flattenAgColumns(columns),
      (col): col is ColDef & { field: string } =>
        Boolean(col.field) && !isSpecialColumn(col)
    ).map((col) => [col.field, col])
  )

/** Create map of new columns */
const createNewColumnsMap = (
  columns: readonly GridColumn[]
): ReadonlyMap<string, GridLeafColumn> =>
  new Map(flattenGridColumns(columns).map((col) => [col.field, col]))

/** Check if new column was added */
const hasNewColumn = (
  newFields: readonly string[],
  currentFields: ReadonlySet<string>
): boolean => newFields.some((field) => !currentFields.has(field))

/** Check if column width changed */
const hasWidthChange = (currentCol: ColDef, newCol: GridLeafColumn): boolean =>
  newCol.width !== undefined && currentCol.width !== newCol.width

/** Check if the column header changed - a relabel the diff must not swallow */
const hasHeaderChange = (currentCol: ColDef, newCol: GridLeafColumn): boolean =>
  currentCol.headerName !== newCol.title

/** Check if the column gained or lost its editor */
const hasEditorChange = (currentCol: ColDef, newCol: GridLeafColumn): boolean =>
  Boolean(currentCol.cellEditor) !== Boolean(newCol.editor)

/** Detect visibility change for a column */
const detectVisibilityChange = (
  currentCol: ColDef,
  newCol: GridLeafColumn
): boolean | null => {
  const currentVisible = !currentCol.hide
  const newVisible = newCol.visible
  return currentVisible === newVisible ? null : newVisible
}

/** Extract visible field names from current columns in order */
const extractVisibleFields = (
  columns: readonly GridColumnDefinition[]
): readonly string[] =>
  pipe(
    flattenAgColumns(columns),
    filter(
      (col): col is ColDef & { field: string } =>
        Boolean(col.field) && !isSpecialColumn(col) && !col.hide
    ),
    map((col) => col.field)
  )

/** Check if column order has changed by comparing field sequences */
const hasColumnOrderChange = (
  currentColumns: readonly GridColumnDefinition[],
  newColumns: readonly GridColumn[]
): boolean => {
  // Extract visible fields in order (excluding special columns)
  const currentVisibleFields = extractVisibleFields(currentColumns)
  const newVisibleFields = pipe(
    flattenGridColumns(newColumns),
    filter((col) => col.visible),
    map((col) => col.field)
  )

  // Different lengths mean structural change
  if (currentVisibleFields.length !== newVisibleFields.length) {
    return true
  }

  // Compare field order
  return currentVisibleFields.some(
    (field, index) => field !== newVisibleFields[index]
  )
}

/** Analyze column changes and collect visibility updates */
type ColumnAnalysis = {
  readonly hasStructuralChange: boolean
  readonly visibilityChanges: ReadonlyMap<string, boolean>
}

type ColumnAnalysisAccumulator = {
  readonly hasStructuralChange: boolean
  readonly visibilityEntries: readonly (readonly [string, boolean])[]
}

const analyzeColumnUpdates = (
  currentColsMap: ReadonlyMap<string, ColDef>,
  newColsMap: ReadonlyMap<string, GridLeafColumn>
): ColumnAnalysis => {
  const analysis = pipe(
    Array.from(newColsMap.entries()),
    reduce(
      (acc, [field, newCol]): ColumnAnalysisAccumulator => {
        const currentCol = currentColsMap.get(field)

        if (!currentCol) {
          return acc
        }

        const visibilityChange = detectVisibilityChange(currentCol, newCol)

        return {
          hasStructuralChange:
            acc.hasStructuralChange ||
            hasWidthChange(currentCol, newCol) ||
            hasHeaderChange(currentCol, newCol) ||
            gridSortField(currentCol.context) !== newCol.sortField ||
            hasEditorChange(currentCol, newCol),
          visibilityEntries:
            visibilityChange === null
              ? acc.visibilityEntries
              : [...acc.visibilityEntries, [field, visibilityChange] as const]
        }
      },
      {
        hasStructuralChange: false,
        visibilityEntries: []
      } as ColumnAnalysisAccumulator
    )
  )

  return {
    hasStructuralChange: analysis.hasStructuralChange,
    visibilityChanges: new Map(analysis.visibilityEntries)
  }
}

export const detectColumnChanges = (
  currentColumns: readonly GridColumnDefinition[],
  newColumns: readonly GridColumn[]
): ColumnChangeType => {
  const currentColsMap = createCurrentColumnsMap(currentColumns)
  const newColsMap = createNewColumnsMap(newColumns)

  // Added or removed fields require column definitions.
  const currentFields = new Set(currentColsMap.keys())
  const newFields = Array.from(newColsMap.keys())

  if (
    hasNewColumn(newFields, currentFields) ||
    currentFields.size !== newFields.length
  ) {
    return { type: 'structural' }
  }

  // Analyze remaining changes
  const analysis = analyzeColumnUpdates(currentColsMap, newColsMap)

  if (analysis.hasStructuralChange) {
    return { type: 'structural' }
  }

  if (hasColumnOrderChange(currentColumns, newColumns)) {
    return { type: 'state-only' }
  }

  return {
    type: 'visibility-only',
    changes: analysis.visibilityChanges
  }
}

/**
 * Column Update Helpers - Pure functions for grid column operations
 */

type VisibilityGroups = {
  readonly toShow: readonly string[]
  readonly toHide: readonly string[]
}

const emptyVisibilityGroups: VisibilityGroups = {
  toShow: [],
  toHide: []
}

const addVisibilityGroup = (
  groups: VisibilityGroups,
  [field, visible]: readonly [string, boolean]
): VisibilityGroups =>
  visible
    ? { ...groups, toShow: [...groups.toShow, field] }
    : { ...groups, toHide: [...groups.toHide, field] }

/** Group column visibility changes into show/hide lists */
const groupColumnsByVisibility = (
  changes: ReadonlyMap<string, boolean>
): VisibilityGroups =>
  pipe(
    Array.from(changes.entries()),
    reduce(addVisibilityGroup, emptyVisibilityGroups)
  )

/** Apply visibility changes to grid using optimized API calls */
export const applyVisibilityChanges = (
  grid: GridApi,
  changes: ReadonlyMap<string, boolean>
): void => {
  if (changes.size === 0) {
    return
  }

  const { toShow, toHide } = groupColumnsByVisibility(changes)

  if (toShow.length > 0) {
    grid.setColumnsVisible([...toShow], true)
  }
  if (toHide.length > 0) {
    grid.setColumnsVisible([...toHide], false)
  }
}

/** Apply visibility and order without replacing existing column definitions. */
export const applyColumnStateChanges = (
  grid: GridApi,
  columns: readonly GridColumn[]
): void => {
  const state = flattenGridColumns(columns).map((column) => ({
    colId: column.field,
    hide: !column.visible
  }))
  const fields = new Set(state.map((column) => column.colId))
  const preserved = grid
    .getColumnState()
    .filter((column) => !fields.has(column.colId))
    .map((column) => ({ colId: column.colId }))

  grid.applyColumnState({ state: [...preserved, ...state], applyOrder: true })
}

/** Apply structural changes to grid columns */
export const applyStructuralChanges = (
  grid: GridApi,
  currentColumns: readonly GridColumnDefinition[],
  newColumns: readonly GridColumn[],
  processColumns: ProcessColumnsFunction,
  gridContext?: { readonly movableRows: boolean; readonly readonly: boolean }
): void => {
  const mergedColumns = mergeColumns(
    currentColumns,
    newColumns,
    processColumns,
    gridContext
  )
  grid.setGridOption('columnDefs', [...mergedColumns] as (
    | ColDef
    | ColGroupDef
  )[])
}

/** Check if columns contain pinned bottom data configuration */
export const shouldUpdatePinnedData = (
  columns: readonly GridColumn[]
): boolean =>
  flattenGridColumns(columns).some((column) => column.bottomCalcParams)

// Export types for external use
export type { ColumnChangeType, ColumnData, ColumnUpdatePayload }
